export const validateField = (name, value, formData = {}, isEditMode = false) => {
    const today = new Date();

    switch (name) {
        case 'firstName':
            if (!value || !value.trim()) return 'First name is required';
            if (value.trim().length < 2) return 'First name must be at least 2 characters';
            if (value.trim().length > 50) return 'First name cannot exceed 50 characters';
            if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'First name can only contain letters and spaces';
            return null;

        case 'lastName':
            // Last name is optional
            if (value && value.trim()) {
                if (value.trim().length < 2) return 'Last name must be at least 2 characters';
                if (value.trim().length > 50) return 'Last name cannot exceed 50 characters';
                if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Last name can only contain letters and spaces';
            }
            return null;

        case 'email':
            if (!value || !value.trim()) return 'Email is required';
            if (!/\S+@\S+\.\S+/.test(value)) return 'Email is invalid';
            if (value.length > 100) return 'Email cannot exceed 100 characters';
            return null;

        case 'mobile':
            if (!value || !value.trim()) return 'Mobile number is required';
            if (!/^\d{10,15}$/.test(value)) return 'Mobile number must be between 10 to 15 digits';
            return null;

        case 'personalEmail':
            if (value && value.trim()) {
                if (!/\S+@\S+\.\S+/.test(value)) return 'Email is invalid';
                if (value.length > 100) return 'Email cannot exceed 100 characters';
            }
            return null;

        case 'personalMobile':
            if (value && value.trim()) {
                if (!/^\d{10,15}$/.test(value)) return 'Mobile number must be between 10 to 15 digits';
            }
            return null;

        case 'dateOfBirth':
            if (!value) return 'Date of birth is required';
            const birthDate = new Date(value);
            if (birthDate > today) return 'Date of birth cannot be in the future';

            const age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            const dayDiff = today.getDate() - birthDate.getDate();
            const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

            if (actualAge > 100) return 'Date of birth seems too old (more than 100 years)';
            if (actualAge < 18) return 'Employee must be at least 18 years old';
            return null;

        case 'gender':
            if (!value) return 'Gender is required';
            return null;

        case 'employeeId':
            if (!value || !value.trim()) return 'Employee ID is required';
            if (value.trim().length > 20) return 'Employee ID cannot exceed 20 characters';
            return null;

        case 'department':
            if (!value) return 'Department is required';
            return null;

        case 'role':
            if (!value) return 'Role is required';
            return null;

        case 'designation':
            if (!value || !value.trim()) return 'Designation is required';
            if (value.trim().length < 2) return 'Designation must be at least 2 characters';
            if (value.trim().length > 100) return 'Designation cannot exceed 100 characters';
            return null;

        case 'location':
            if (!value) return 'Location is required';
            return null;

        case 'dateOfJoining':
            if (!value) return 'Date of joining is required';
            if (!isEditMode) {
                const joiningDate = new Date(value);
                const oneYearFromNow = new Date(today);
                oneYearFromNow.setFullYear(today.getFullYear() + 1);
                if (joiningDate > oneYearFromNow) return 'Date of joining cannot be more than 1 year in the future';
            }
            return null;

        case 'employmentType':
            if (!value) return 'Employment type is required';
            return null;

        case 'shift':
            if (!value) return 'Shift is required';
            return null;

        case 'pan':
            if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) return 'PAN must be in format: ABCDE1234F';
            return null;

        case 'aadhaar':
            if (!value) return 'Aadhaar Number is required';
            if (!/^\d{12}$/.test(value)) return 'Aadhaar must be exactly 12 digits';
            return null;

        case 'uan':
            if (value && !/^\d{12}$/.test(value)) return 'UAN must be exactly 12 digits';
            return null;

        case 'esi':
            if (value && !/^\d{10}$/.test(value)) return 'ESI must be exactly 10 digits';
            return null;

        case 'bankAccount':
            if (value && !/^\d{9,18}$/.test(value)) return 'Bank account must be 9-18 digits';
            return null;

        case 'ifsc':
            if (value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) return 'IFSC must be in format: ABCD0123456';
            return null;

        case 'postalCode':
            // Validation for Postal Code - alphanumeric, spaces allowed, max 15 chars
            if ((formData.country || value) && value) {
                if (value.length > 15) return 'Postal Code cannot exceed 15 characters';
                if (!/^[a-zA-Z0-9\s]+$/.test(value.trim())) return 'Postal Code must contain only letters and numbers';
            }
            return null;

        case 'permanentPostalCode':
            if ((formData.permanentCountry || value) && value) {
                if (value.length > 15) return 'Postal Code cannot exceed 15 characters';
                if (!/^[a-zA-Z0-9\s]+$/.test(value.trim())) return 'Postal Code must contain only letters and numbers';
            }
            return null;

        case 'state':
            // State is optional when Country is selected
            return null;

        case 'city':
            // City is optional when Country/State is selected
            return null;

        case 'permanentState':
            // Permanent State is optional
            return null;

        case 'permanentCity':
            // Permanent City is optional
            return null;

        default:
            return null;
    }
};

export const validateEmployeeForm = (formData, isEditMode = false) => {
    const errors = {};

    // List of fields to validate
    const fields = [
        'firstName', 'lastName', 'email', 'personalEmail', 'mobile', 'personalMobile', 'dateOfBirth', 'gender',
        'employeeId', 'department', 'role', 'designation', 'location',
        'dateOfJoining', 'employmentType', 'shift',
        'pan', 'aadhaar', 'uan', 'esi', 'bankAccount', 'ifsc',
        'postalCode', 'state', 'city', 'permanentPostalCode', 'permanentState', 'permanentCity'
    ];

    fields.forEach(field => {
        const error = validateField(field, formData[field], formData, isEditMode);
        if (error) {
            errors[field] = error;
        }
    });

    return errors;
};
